import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiSearch, FiTool, FiEdit3, FiArrowRight, FiZap, FiCode, 
  FiLayers, FiMonitor, FiSmartphone, FiTablet, FiDownload, 
  FiCopy, FiEye, FiSettings, FiPalette, FiType, FiImage,
  FiCheck, FiPlay, FiShuffle, FiRefreshCw, FiGlobe, FiLock, FiX
} = FiIcons;

function FeaturesPage() {
  const [activeTab, setActiveTab] = useState('clone');

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const mainFeatures = [
    {
      icon: FiSearch,
      title: 'Smart Website Cloning',
      description: 'Clone any live website with perfect accuracy, preserving layout, styling, and animations.',
      color: 'purple',
      features: [
        'AI-powered element detection',
        'Preserves responsive breakpoints',
        'Captures animations and interactions',
        'Maintains semantic HTML structure'
      ]
    },
    {
      icon: FiEdit3,
      title: 'Visual Editor',
      description: 'Edit cloned websites with our intuitive visual editor before exporting.',
      color: 'blue',
      features: [
        'Drag-and-drop interface',
        'Real-time preview',
        'Component-based editing',
        'Undo/redo functionality'
      ]
    },
    {
      icon: FiTool,
      title: 'Multi-Platform Export',
      description: 'Export to your favorite platform or download clean, production-ready code.',
      color: 'green',
      features: [
        'Webflow export',
        'Framer export',
        'Clean HTML/CSS',
        'React components'
      ]
    }
  ];

  const advancedFeatures = [
    {
      icon: FiLayers,
      title: 'Element Library',
      description: 'Save and organize individual elements for reuse across projects.',
      demo: 'Organize components by category and project'
    },
    {
      icon: FiPalette,
      title: 'Design System Extraction',
      description: 'Automatically extract colors, fonts, and spacing from cloned sites.',
      demo: 'Generate style guides from any website'
    },
    {
      icon: FiCode,
      title: 'Clean Code Generation',
      description: 'Get optimized, semantic code that follows best practices.',
      demo: 'Production-ready HTML, CSS, and JavaScript'
    },
    {
      icon: FiMonitor,
      title: 'Responsive Cloning',
      description: 'Clone websites with full responsive behavior intact.',
      demo: 'Perfect mobile, tablet, and desktop layouts'
    },
    {
      icon: FiRefreshCw,
      title: 'Live Sync',
      description: 'Keep your clones updated when the original site changes.',
      demo: 'Automatic updates and change notifications'
    },
    {
      icon: FiGlobe,
      title: 'Cross-Browser Testing',
      description: 'Test your cloned sites across different browsers automatically.',
      demo: 'Chrome, Firefox, Safari, and Edge compatibility'
    }
  ];

  const workflowTabs = [
    {
      id: 'clone',
      title: 'Clone',
      icon: FiCopy,
      description: 'Enter any URL and watch as ClonewebX captures every detail'
    },
    {
      id: 'edit',
      title: 'Edit',
      icon: FiEdit3,
      description: 'Customize the cloned design with our visual editor'
    },
    {
      id: 'export',
      title: 'Export',
      icon: FiDownload,
      description: 'Export to your platform of choice or download the code'
    }
  ];

  const integrations = [
    { name: 'Webflow', logo: '🌐', description: 'Direct export to Webflow projects' },
    { name: 'Framer', logo: '🎨', description: 'Seamless Framer integration' },
    { name: 'Figma', logo: '📐', description: 'Export designs to Figma' },
    { name: 'VS Code', logo: '💻', description: 'Clean code for developers' },
    { name: 'GitHub', logo: '🐙', description: 'Version control integration' },
    { name: 'Vercel', logo: '▲', description: 'One-click deployment' }
  ];

  return (
    <div className="pt-16">
      {/* Header Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mb-6">
              Every feature you need to <span className="text-purple-600">clone like a pro</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              From smart cloning to advanced editing, ClonewebX provides all the tools you need to replicate and customize any website
            </p>
            <button className="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-all transform hover:scale-105 flex items-center gap-2 mx-auto">
              Try All Features Free
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Main Features */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="grid lg:grid-cols-3 gap-8"
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            {mainFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${
                  feature.color === 'purple' ? 'bg-purple-100' :
                  feature.color === 'blue' ? 'bg-blue-100' : 'bg-green-100'
                }`}>
                  <SafeIcon icon={feature.icon} className={`w-8 h-8 ${
                    feature.color === 'purple' ? 'text-purple-600' :
                    feature.color === 'blue' ? 'text-blue-600' : 'text-green-600'
                  }`} />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-4">{feature.title}</h3>
                <p className="text-slate-600 mb-6">{feature.description}</p>
                <div className="space-y-3">
                  {feature.features.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center gap-3">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Interactive Workflow */}
      <section className="py-20 bg-slate-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              See ClonewebX in Action
            </h2>
            <p className="text-xl text-slate-600">
              Experience our three-step workflow that transforms any website into your custom design
            </p>
          </motion.div>

          {/* Workflow Tabs */}
          <div className="flex justify-center mb-12">
            <div className="bg-white rounded-lg p-2 shadow-sm border border-slate-200">
              {workflowTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-3 px-6 py-3 rounded-md font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <SafeIcon icon={tab.icon} className="w-5 h-5" />
                  {tab.title}
                </button>
              ))}
            </div>
          </div>

          {/* Workflow Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            {activeTab === 'clone' && (
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Smart Website Cloning</h3>
                  <p className="text-slate-600 mb-6">
                    Simply paste any URL and watch ClonewebX intelligently analyze and replicate the entire website structure, styling, and functionality.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold">1</div>
                      <span className="text-slate-700">Enter the website URL</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold">2</div>
                      <span className="text-slate-700">AI analyzes the page structure</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold">3</div>
                      <span className="text-slate-700">Perfect clone ready in seconds</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6">
                  <div className="bg-white rounded-md p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    </div>
                    <div className="text-sm text-slate-600">https://example.com</div>
                  </div>
                  <div className="text-center text-purple-600 font-semibold mb-2">
                    Cloning in progress...
                  </div>
                  <div className="w-full bg-purple-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full w-3/4 animate-pulse"></div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'edit' && (
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Visual Editor</h3>
                  <p className="text-slate-600 mb-6">
                    Customize every aspect of your cloned website with our intuitive visual editor. Change colors, fonts, content, and layout without touching code.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiPalette} className="w-5 h-5 text-purple-600" />
                      <span className="text-slate-700">Color palette customization</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiType} className="w-5 h-5 text-purple-600" />
                      <span className="text-slate-700">Typography controls</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiImage} className="w-5 h-5 text-purple-600" />
                      <span className="text-slate-700">Image replacement</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiSettings} className="w-5 h-5 text-purple-600" />
                      <span className="text-slate-700">Layout adjustments</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-lg p-6">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white rounded p-3 text-center">
                      <div className="w-full h-16 bg-purple-100 rounded mb-2"></div>
                      <div className="text-xs text-slate-600">Header</div>
                    </div>
                    <div className="bg-white rounded p-3 text-center">
                      <div className="w-full h-16 bg-blue-100 rounded mb-2"></div>
                      <div className="text-xs text-slate-600">Content</div>
                    </div>
                  </div>
                  <div className="text-center text-blue-600 font-semibold">
                    <SafeIcon icon={FiEdit3} className="w-5 h-5 inline mr-2" />
                    Editing Mode Active
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'export' && (
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Multi-Platform Export</h3>
                  <p className="text-slate-600 mb-6">
                    Export your customized design to any platform or download clean, production-ready code. One click deployment to your favorite tools.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {['Webflow', 'Framer', 'HTML/CSS', 'React'].map((platform) => (
                      <div key={platform} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                        <span className="text-slate-700 text-sm">{platform}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-purple-50 rounded-lg p-6">
                  <div className="space-y-3">
                    <button className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2">
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      Export to Webflow
                    </button>
                    <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2">
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      Download HTML/CSS
                    </button>
                    <button className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2">
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      Export to Framer
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Advanced Features Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Advanced Features for Power Users
            </h2>
            <p className="text-xl text-slate-600">
              Go beyond basic cloning with professional tools and automation
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            {advancedFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={feature.icon} className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">{feature.title}</h3>
                <p className="text-slate-600 mb-4">{feature.description}</p>
                <div className="text-sm text-purple-600 font-medium">{feature.demo}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-20 bg-slate-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Seamless Integrations
            </h2>
            <p className="text-xl text-slate-600">
              Connect with your favorite design and development tools
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 lg:grid-cols-6 gap-6"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            {integrations.map((integration, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-lg p-6 text-center shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300"
              >
                <div className="text-3xl mb-3">{integration.logo}</div>
                <h3 className="font-semibold text-slate-800 mb-2">{integration.name}</h3>
                <p className="text-xs text-slate-600">{integration.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Why Choose ClonewebX?
            </h2>
            <p className="text-xl text-slate-600">
              See how we compare to traditional development methods
            </p>
          </motion.div>

          <motion.div
            className="bg-white rounded-2xl shadow-lg overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="grid md:grid-cols-3 divide-x divide-slate-200">
              {/* Traditional Development */}
              <div className="p-8">
                <h3 className="text-lg font-semibold text-slate-800 mb-6">Traditional Development</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">Weeks of development time</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">Requires coding expertise</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">High development costs</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">Manual responsive design</span>
                  </div>
                </div>
              </div>

              {/* ClonewebX */}
              <div className="p-8 bg-gradient-to-br from-purple-50 to-blue-50">
                <h3 className="text-lg font-semibold text-purple-800 mb-6">ClonewebX</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">Minutes to clone and customize</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">No coding required</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">Affordable monthly pricing</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">Perfect responsive cloning</span>
                  </div>
                </div>
              </div>

              {/* Manual Copying */}
              <div className="p-8">
                <h3 className="text-lg font-semibold text-slate-800 mb-6">Manual Copying</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">Time-consuming process</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">Error-prone and incomplete</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">Missing animations & interactions</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">No automation or optimization</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Experience all features with a free trial
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              No credit card required. Start cloning websites in under 60 seconds.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-all transform hover:scale-105 flex items-center justify-center gap-2">
                Start Free Trial
                <SafeIcon icon={FiPlay} className="w-5 h-5" />
              </button>
              <button className="border-2 border-slate-300 text-slate-700 px-8 py-4 rounded-lg font-semibold text-lg hover:border-slate-400 transition-all flex items-center justify-center gap-2">
                <SafeIcon icon={FiEye} className="w-5 h-5" />
                Watch Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default FeaturesPage;